import { LineBlock as LineBlockType } from '../../types/panel'

/* Serien-Farben strikt aus den Q90-Tokens */
const SERIES_COLORS = ['var(--green-900)', 'var(--green-500)', 'var(--gold)', 'var(--red)']

interface Props { block: LineBlockType }

/** Liniendiagramm im Kontor-Stil (SVG, Mono-Achsen, Token-Farben). */
export default function LineBlock({ block }: Props) {
  const W = 760, H = 300, padL = 52, padR = 16, padT = 18, padB = 34
  const labels = Array.isArray(block.labels) ? block.labels : []
  const series = (Array.isArray(block.series) ? block.series : [])
    .map(s => ({ ...s, values: Array.isArray(s.values) ? s.values : [] }))
  const n = labels.length
  if (n === 0 || series.length === 0) return null

  const allVals = series.flatMap(s => s.values).filter(v => v != null)
  const rawMax = Math.max(...allVals, 1)
  const maxV = Math.ceil(rawMax / Math.pow(10, Math.floor(Math.log10(rawMax)))) *
    Math.pow(10, Math.floor(Math.log10(rawMax)))

  const x = (i: number) => padL + (n === 1 ? 0 : (i * (W - padL - padR)) / (n - 1))
  const y = (v: number) => padT + (1 - v / maxV) * (H - padT - padB)
  const gridLines = [0.25, 0.5, 0.75, 1]

  // Bei vielen Punkten nur jedes n-te X-Label anzeigen
  const labelStep = Math.max(1, Math.ceil(n / 12))

  return (
    <div className="k-block">
      <h4 className="k-block__title">{block.title}</h4>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={block.title}>
        {gridLines.map(g => (
          <g key={g}>
            <line
              x1={padL} x2={W - padR} y1={y(maxV * g)} y2={y(maxV * g)}
              stroke="var(--k-rule)" strokeWidth="1" strokeDasharray={g === 1 ? '' : '3 4'}
            />
            <text
              x={padL - 8} y={y(maxV * g) + 3} textAnchor="end" fontSize="10"
              fontFamily="var(--font-mono)" fill="var(--k-fg-2)"
            >
              {Math.round(maxV * g).toLocaleString('de-DE')}
            </text>
          </g>
        ))}
        <line x1={padL} x2={W - padR} y1={H - padB} y2={H - padB} stroke="var(--k-head)" strokeWidth="1" />

        {series.map((s, si) => {
          const pts = s.values
            .map((v, i) => (v == null ? null : `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`))
            .filter(Boolean)
            .join(' ')
          const color = SERIES_COLORS[si % SERIES_COLORS.length]
          return (
            <g key={s.label}>
              {si === 0 && !s.dashed && (
                <path
                  d={pts + ` L${x(n - 1).toFixed(1)},${H - padB} L${padL},${H - padB} Z`}
                  fill="var(--green-500)" opacity="0.12"
                />
              )}
              <path d={pts} fill="none" stroke={color} strokeWidth="2" strokeDasharray={s.dashed ? '5 5' : undefined} />
              {n <= 24 && s.values.map((v, i) => v == null ? null : (
                <circle key={i} cx={x(i)} cy={y(v)} r={3} fill={color} />
              ))}
            </g>
          )
        })}

        {labels.map((l, i) => (
          i % labelStep === 0 ? (
            <text
              key={i} x={x(i)} y={H - padB + 16} textAnchor="middle" fontSize="10"
              fontFamily="var(--font-mono)" fill="var(--k-fg-2)" style={{ textTransform: 'uppercase' }}
            >
              {l}
            </text>
          ) : null
        ))}
      </svg>
      {series.length > 1 && (
        <div className="k-chart-note">
          {series.map((s, si) => (
            <span key={s.label} style={{ color: SERIES_COLORS[si % SERIES_COLORS.length] }}>
              {s.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
